import React, { useMemo } from 'react';
import { C, S, planColors as PLAN_COLORS } from '../../utils/styles';
import Icon from '../Icon';
import { useConfig } from '../../context/ConfigContext';
import { optimizeEmployeeWithEligibility } from '../../utils/calculations';

/**
 * Compact Enrollment Progress card.
 *
 * Tighter than the original — 90px donut, single-line response stat, inline
 * legend pills, optional small plan-distribution row underneath. Designed to
 * sit comfortably alongside the wider Section 125 Summary on the right.
 */
export default function EnrollmentProgressSection({ client }) {
  const { config } = useConfig();
  const employees = client?.employees || [];
  const hasCensus = employees.length > 0;

  const enrolled = employees.filter((e) => e.enrollmentStatus === 'enrolled');
  const declined = employees.filter((e) => e.enrollmentStatus === 'declined');
  const pending = employees.filter((e) => (e.portalInviteSentAt || e.enrollmentInvited) && !e.enrollmentStatus);

  const totalEligible = useMemo(() => {
    if (!hasCensus || !config) return 0;
    try {
      let count = 0;
      employees.forEach((e) => {
        const salaryNum = Number(e.salary) || Number(e.annualSalary) || 0;
        if (!salaryNum) return;
        const a = optimizeEmployeeWithEligibility({ ...e, salary: salaryNum }, config);
        if (a.hasViable && a.optimal) count += 1;
      });
      return count;
    } catch (err) {
      return employees.filter((e) => e.eligible !== false).length;
    }
  }, [employees, config, hasCensus]);

  const notInvitedCount = useMemo(() => {
    if (!config) return 0;
    const invitedSet = new Set(
      employees.filter((e) => e.portalInviteSentAt || e.enrollmentInvited || e.enrollmentStatus).map((e) => e.id || e.email)
    );
    let stillToInvite = 0;
    employees.forEach((e) => {
      const salaryNum = Number(e.salary) || Number(e.annualSalary) || 0;
      if (!salaryNum) return;
      try {
        const a = optimizeEmployeeWithEligibility({ ...e, salary: salaryNum }, config);
        if (a.hasViable && a.optimal && !invitedSet.has(e.id || e.email)) stillToInvite += 1;
      } catch (err) { /* noop */ }
    });
    return stillToInvite;
  }, [employees, config]);

  const responseCount = enrolled.length + declined.length;
  const respondedPct = totalEligible > 0 ? Math.round((responseCount / totalEligible) * 100) : 0;

  const planCounts = enrolled.reduce((acc, e) => {
    const p = e.enrolledPlan || e.optimalPlan || 'Unknown';
    acc[p] = (acc[p] || 0) + 1;
    return acc;
  }, {});
  const maxPlanCount = Math.max(1, ...Object.values(planCounts));

  if (!hasCensus) {
    return (
      <div data-testid="enrollment-progress-section" style={{ ...S.card, padding: 18, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <SectionHeader title="Enrollment Progress" icon="people-outline" />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
          <Placeholder
            icon="cloud-upload-outline"
            title="Upload your census to see enrollment"
            body="Once your census is uploaded, we'll track invitations and responses here."
          />
        </div>
      </div>
    );
  }

  const noEnrollmentYet = responseCount === 0 && pending.length === 0;

  return (
    <div
      data-testid="enrollment-progress-section"
      style={{ ...S.card, padding: 20, height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <SectionHeader title="Enrollment Progress" icon="people-outline" />

      {/* Donut centered at the top */}
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 8px' }}>
        <DonutChart
          enrolled={enrolled.length}
          declined={declined.length}
          pending={pending.length}
          notInvited={notInvitedCount}
          totalEligible={totalEligible}
          respondedPct={respondedPct}
          greyedOut={noEnrollmentYet}
        />
      </div>

      {/* Response stat */}
      <div style={{ textAlign: 'center', marginTop: 4 }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: C.text, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.05, fontVariantNumeric: 'tabular-nums' }}>
          {responseCount} <span style={{ color: C.textLight, fontWeight: 600 }}>of</span> {totalEligible}
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.textMid, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 4 }}>
          eligible employees responded
        </div>
      </div>

      {/* Legend pills row */}
      <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 8, marginTop: 14, fontSize: 11 }}>
        <LegendPill color="#059669" label="enrolled" value={enrolled.length} testId="legend-enrolled" />
        <LegendPill color="#dc2626" label="declined" value={declined.length} testId="legend-declined" />
        <LegendPill color="#d1d5db" label="pending" value={pending.length} testId="legend-pending" />
        {notInvitedCount > 0 && <LegendPill color="#9ca3af" label="not invited" value={notInvitedCount} testId="legend-notinvited" />}
      </div>

      {/* Spacer to push the info note to the bottom of the card */}
      <div style={{ flex: 1 }} />

      {noEnrollmentYet && (
        <div
          style={{
            marginTop: 14,
            padding: '10px 12px',
            background: C.bg,
            borderRadius: 8,
            fontSize: 11,
            color: C.textMid,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            lineHeight: 1.4,
          }}
        >
          <Icon name="information-circle-outline" style={{ fontSize: 14, color: C.textLight, marginTop: 1, flexShrink: 0 }} />
          <span>Enrollment begins after census analysis and invitations are sent.</span>
        </div>
      )}

      {enrolled.length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.borderLight}` }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.textLight, marginBottom: 8 }}>
            Plan Distribution
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {Object.entries(planCounts).sort((a, b) => b[1] - a[1]).map(([plan, count]) => (
              <PlanBar key={plan} plan={plan} count={count} max={maxPlanCount} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ title, icon }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
      <Icon name={icon} style={{ fontSize: 18, color: C.accent }} />
      <h2 style={{ fontSize: 14, fontWeight: 800, color: C.text, margin: 0, letterSpacing: '-0.01em', textTransform: 'uppercase' }}>{title}</h2>
    </div>
  );
}

function Placeholder({ icon, title, body }) {
  return (
    <div style={{ padding: '22px 12px', textAlign: 'center', background: C.bg, borderRadius: 10 }}>
      <Icon name={icon} style={{ fontSize: 26, color: C.textLight, marginBottom: 8 }} />
      <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 2 }}>{title}</div>
      <div style={{ fontSize: 11, color: C.textMid }}>{body}</div>
    </div>
  );
}

function LegendPill({ color, label, value, testId }) {
  return (
    <div data-testid={testId} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 8px', background: C.bg, borderRadius: 999 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: C.text }}>{value}</span>
      <span style={{ color: C.textMid }}>{label}</span>
    </div>
  );
}

function DonutChart({ enrolled, declined, pending, notInvited, respondedPct, greyedOut }) {
  const size = 150;
  const stroke = 16;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const total = Math.max(1, enrolled + declined + pending + notInvited);

  const segments = [
    { value: enrolled, color: greyedOut ? '#e5e7eb' : '#059669' },
    { value: declined, color: greyedOut ? '#e5e7eb' : '#dc2626' },
    { value: pending, color: greyedOut ? '#e5e7eb' : '#d1d5db' },
    { value: notInvited, color: '#f0f2f5' },
  ].filter((s) => s.value > 0);

  let offset = 0;
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.bg} strokeWidth={stroke} />
        {segments.map((s, i) => {
          const fraction = s.value / total;
          const dash = fraction * circumference;
          const c = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={stroke}
              strokeDasharray={`${dash} ${circumference}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
              style={{ transition: 'stroke-dasharray 0.4s ease' }}
            />
          );
          offset += dash;
          return c;
        })}
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
        <div style={{ fontSize: 32, fontWeight: 800, color: greyedOut ? C.textLight : C.accent, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>
          {respondedPct}%
        </div>
        <div style={{ fontSize: 9, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 4, fontWeight: 700 }}>responded</div>
      </div>
    </div>
  );
}

function PlanBar({ plan, count, max }) {
  const pct = Math.max(4, Math.round((count / max) * 100));
  const color = PLAN_COLORS[plan] || C.accent;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
      <div style={{ width: 70, color: C.textMid, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{plan}</div>
      <div style={{ flex: 1, height: 10, background: C.bg, borderRadius: 5, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 5, transition: 'width 0.3s ease' }} />
      </div>
      <div style={{ width: 22, textAlign: 'right', color: C.textMid, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>{count}</div>
    </div>
  );
}
